import config from '@root/config'

export interface SpecialPaymentLine {
    name: string
    meta?: string
    period?: string
    amount: number | string
}
export interface SpecialPaymentSnapshot {
    version: 1
    paymentPayloadId: number
    projectId: string
    projectName: string
    tariffId: string
    tariffName: string
    period: string
    renewalMode?: 'standalone' | 'with_tariff'
    extraChannels?: number
    promoCode?: string
    activeFrom?: string
    activeUntil?: string
    effectiveMode?: 'immediate' | 'at_period_end'
    effectiveAt?: string
    requiresScheduledTariffChange?: boolean
    lines: SpecialPaymentLine[]
    price: Record<string, any>
}

export function formatSpecialPaymentPrice(value: number | string | null | undefined): string {
    const amount = Number(value || 0)
    if (!Number.isFinite(amount)) return '0 ₽'
    const roundedToKopecks = Math.round(amount * 100) / 100
    const hasKopecks = Math.abs(Math.round(roundedToKopecks * 100)) % 100 !== 0
    return `${new Intl.NumberFormat('ru-RU', {
        minimumFractionDigits: hasKopecks ? 2 : 0,
        maximumFractionDigits: hasKopecks ? 2 : 0,
    }).format(roundedToKopecks)} ₽`
}

export function formatSpecialPaymentDate(value?: string | Date | null): string {
    if (!value) return '—'
    const date = value instanceof Date ? value : new Date(value)
    if (Number.isNaN(date.getTime())) return String(value)
    return date.toLocaleDateString('ru-RU')
}

// Ссылка на оплату заказа без входа: token подписывает пару проект + заказ на backend.
export interface SpecialPaymentLink {
    projectId: string
    paymentPayloadId: number
    token: string
}

export function buildSpecialPaymentUrl(link: SpecialPaymentLink): string {
    const base = `${window.location.origin}/project/${encodeURIComponent(link.projectId)}/payment/${link.paymentPayloadId}`
    return `${base}?t=${encodeURIComponent(link.token)}`
}

// ЮKassa возвращает плательщика на страницу биллинга проекта, куда без доступа к проекту
// не пускают. Запоминаем ссылку в этой вкладке, чтобы вернуть его на страницу оплаты.
const SPECIAL_PAYMENT_RETURN_KEY = 'specialPaymentLink:'

export function rememberSpecialPaymentReturn(link: SpecialPaymentLink): void {
    try {
        window.sessionStorage.setItem(
            `${SPECIAL_PAYMENT_RETURN_KEY}${link.paymentPayloadId}`,
            JSON.stringify({ projectId: link.projectId, token: link.token })
        )
    } catch (_) {
        // Без хранилища плательщик после ЮKassa просто попадёт в биллинг, как раньше.
    }
}

// Одноразово: следующий заход в биллинг проекта уже не уводит на страницу оплаты.
export function takeSpecialPaymentReturn(projectId: string, paymentPayloadId: number): SpecialPaymentLink | null {
    try {
        const key = `${SPECIAL_PAYMENT_RETURN_KEY}${paymentPayloadId}`
        const raw = window.sessionStorage.getItem(key)
        window.sessionStorage.removeItem(key)
        const saved = raw ? JSON.parse(raw) : null
        if (!saved?.token || saved.projectId !== projectId) return null
        return { projectId, paymentPayloadId, token: String(saved.token) }
    } catch (_) {
        return null
    }
}

export function isBillingManager(userId?: string | null): boolean {
    return Boolean(userId && Array.isArray(config.adminsWhitelist) && config.adminsWhitelist.includes(userId))
}

const SPECIAL_PAYMENT_CHANNEL_NAMES: Record<string, string> = {
    whatsappchannel: 'WhatsApp',
    whatsappchannelslot: 'WhatsApp',
    telegramchannel: 'Telegram',
    telegramchannelslot: 'Telegram',
    smschannel: 'SMS',
    smschannelslot: 'SMS',
    maxchannel: 'MAX',
    maxslot: 'MAX',
    maxbusiness: 'MAX Business',
    maxbusinessslot: 'MAX Business',
    maxbusinesschannel: 'MAX Business',
}

export function formatSpecialPaymentLineName(value?: string | null, kind?: string | null): string {
    const name = String(value || '').trim()
    if (!name) return kind === 'slot' ? 'Свободный канал' : 'Позиция платежа'
    const technicalName = name.toLowerCase().replace(/[\s_-]+/g, '')
    return SPECIAL_PAYMENT_CHANNEL_NAMES[technicalName] || name
}

function paymentLineKind(item: any): string | undefined {
    if (item?.kind) return String(item.kind)
    if (item?.integrationId === 'channelSlot') return 'slot'
    return undefined
}

export function mapPaymentLines(data: any): SpecialPaymentLine[] {
    const lineItems = Array.isArray(data?.price?.lineItems) ? data.price.lineItems : []
    if (lineItems.length) {
        return lineItems.map((item: any) => ({
            name: formatSpecialPaymentLineName(item.name || item.title, paymentLineKind(item)),
            meta: item.kind === 'tariff' || item.isTariff
                ? 'Тариф'
                : (item.inTariff
                    ? 'Входит в тариф'
                    : (paymentLineKind(item) === 'slot' ? 'Доп. канал' : 'Модуль')),
            period: item.periodFrom && item.periodTo
                ? `${formatSpecialPaymentDate(item.periodFrom)}—${formatSpecialPaymentDate(item.periodTo)}`
                : undefined,
            amount: Number(item.amount ?? item.total ?? 0),
        }))
    }
    return [{
        name: data?.tariffName || data?.name || 'Тариф',
        meta: 'Тариф',
        period: data?.activeFrom && data?.activeUntil
            ? `${formatSpecialPaymentDate(data.activeFrom)}—${formatSpecialPaymentDate(data.activeUntil)}`
            : undefined,
        amount: Number(data?.price?.toPay ?? data?.price?.needToPay ?? 0),
    }]
}
